import { React } from 'react';
import RoundedButton from '../../RoundedButton';

const createTableStyles = `
    .font-century-gothic {
        font-family: 'Century Gothic', sans-serif;
    }

    .bg-light-grey {
        background-color: #D9D9D9;
    }

    /* Melhora a visibilidade do cabeçalho fixo */
    :root {
        --table-border: 1px solid #adb5bd;
    }

    .table-grid {
        max-height: 500px;
        border: var(--table-border);
        overflow: auto;
    }

    .table-custom {
        border-collapse: separate;
        border-spacing: 0;
        width: 100%;
    }

    /* Bordas consistentes para todas as células */
    .table-custom th, .table-custom td {
        border-right: var(--table-border);
        border-bottom: var(--table-border);
        padding: 12px;
    }

    /* Fixa o cabeçalho no topo */
    .table-custom thead th {
        position: sticky;
        top: 0;
        background-color: #D9D9D9;
        z-index: 20;
        min-width: 250px; /* Garante que a coluna não esmague */
    }

    /* Fixa a primeira coluna na esquerda */
    .table-custom td:first-child, 
    .table-custom th:first-child {
        position: sticky;
        left: 0;
        z-index: 25;
        background-color: #D9D9D9;
        min-width: 250px;
        border-right: 2px solid #adb5bd !important; /* Divisória mais forte na lateral fixa */
    }

    /* Reseta fundo da primeira coluna no corpo para não sobrepor o sticky */
    .table-custom tbody td:first-child {
        background-color: #ececec;
    }

    .table-textarea {
        background: transparent;
        border: 1px solid transparent;
        width: 100%;
        resize: none;
        overflow-y: auto;
        max-height: 4.5em;
        line-height: 1.5em;
        font-weight: 500;
        text-align: inherit;
    }

    .table-textarea:focus {
        outline: none;
        background-color: #fff;
        border: 1px solid #4E9BB9;
        border-radius: 4px;
    }

    .placeholder-cell {
        background-color: #ffffff !important;
        color: #adb5bd;
        font-style: italic;
        text-align: center;
        vertical-align: middle;
        min-width: 250px;
    }
`;

/**
 * Componente responsável por criar um campo de entrada para uma tabela.
 * @param {Object} props - Propriedades do componente.
 * @param {Object} props.group - Grupo de itens da tabela.
 * @param {number} props.groupIndex - Índice do grupo dentro da página.
 * @param {number} props.pageIndex - Índice da página.
 * @param {Function} props.insertItem - Função para adicionar um novo item na tabela.
 * @param {Function} props.updateItem - Função para atualizar um item na tabela.
 * @param {Function} props.removeItem - Função para remover um item da tabela.
 * @param {Function} props.insertTableColumn - Função para adicionar uma nova coluna na tabela.
 * @param {Function} props.updateTableColumn - Função para atualizar uma coluna da tabela.
 * @param {Function} props.removeTableColumn - Função para remover uma coluna da tabela.
 */
function CreateTableInput(props) {
    const { group, groupIndex, pageIndex, insertItem, updateItem, removeItem, insertTableColumn, updateTableColumn, removeTableColumn } =
        props;

    const changeItem = (event, itemIndex) => {
        const updatedText = event.target.value;
        const updatedItems = group.items.map((currentItem, index) => {
            if (index === itemIndex) return { ...currentItem, text: updatedText };
            return currentItem;
        });
        updateItem(updatedItems[itemIndex], itemIndex);
    };

    return (
        <div className="pb-4 pb-lg-5">
            <div className="bg-light-grey rounded-4 w-100 p-4 shadow-sm">
                <div className="table-grid">
                    <table className="table-custom font-barlow">
                        <thead>
                            <tr>
                                <th className="align-middle">
                                    <div className="d-flex align-items-center justify-content-center gap-3">
                                        <div className="d-flex flex-column align-items-center">
                                            <RoundedButton
                                                className="text-white"
                                                hsl={[190, 46, 70]}
                                                icon="add_row_below"
                                                onClick={() => insertItem('TABLEROW', pageIndex, groupIndex)}
                                            />
                                            <span className="fw-semibold mt-1">Linha</span>
                                        </div>
                                        <div className="d-flex flex-column align-items-center">
                                            <RoundedButton
                                                className="text-white"
                                                hsl={[190, 46, 70]}
                                                icon="add_column_right"
                                                onClick={() => insertTableColumn()}
                                            />
                                            <span className="fw-semibold mt-1">Coluna</span>
                                        </div>
                                    </div>
                                </th>
                                {group.tableColumns?.map((column, columnIndex) => (
                                    <th key={'column-' + columnIndex}>
                                        <div className="d-flex flex-column gap-1">
                                            <div className="d-flex justify-content-end">
                                                <RoundedButton
                                                    className="text-white"
                                                    hsl={[0, 0, 40]}
                                                    size={26}
                                                    icon="close"
                                                    onClick={() => removeTableColumn(columnIndex)}
                                                />
                                            </div>
                                            <textarea
                                                className="table-textarea fw-medium text-center"
                                                rows="2"
                                                value={column.text || ''}
                                                placeholder="Título da Coluna..."
                                                onChange={(e) => updateTableColumn(columnIndex, e.target.value)}
                                            />
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {group.items?.map((item, itemIndex) => (
                                <tr key={'item-line-' + itemIndex}>
                                    <td className="align-middle">
                                        <div className="d-flex align-items-center gap-2">
                                            <textarea
                                                className="table-textarea fw-medium text-center"
                                                rows="2"
                                                value={item.text || ''}
                                                placeholder="Título da Linha..."
                                                onChange={(event) => changeItem(event, itemIndex)}
                                            />
                                            <RoundedButton
                                                className="text-white"
                                                hsl={[0, 0, 40]}
                                                size={26}
                                                icon="delete"
                                                onClick={() => removeItem(itemIndex)}
                                            />
                                        </div>
                                    </td>
                                    {group.tableColumns?.map((_, colIdx) => (
                                        <td key={colIdx} className="placeholder-cell">
                                            <span>Resposta</span>
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            <style>{createTableStyles}</style>
        </div>
    );
}

export default CreateTableInput;
